import { type ExprNode, type FuncName, num, add, mul, pow, func, cloneDeep } from './node';

/**
 * Symbolically differentiate `node` with respect to `variable`.
 *
 * Returns an unsimplified ExprNode.  Pass the result to simplify() (Task 5)
 * to get a canonical form.  The returned tree contains no opaque nodes and is
 * itself differentiable, enabling second (and higher) derivatives.
 */
export function derivative(node: ExprNode, variable: string): ExprNode {
  const D = (n: ExprNode): ExprNode => derivative(n, variable);

  switch (node.kind) {
    // -----------------------------------------------------------------------
    // Primitive nodes
    // -----------------------------------------------------------------------

    case 'num': {
      return num(0);
    }

    case 'var': {
      return node.name === variable ? num(1) : num(0);
    }

    // -----------------------------------------------------------------------
    // Sum rule:  d/dx[u + v + …] = u' + v' + …
    // -----------------------------------------------------------------------

    case 'add': {
      return add(node.children.map(D));
    }

    // -----------------------------------------------------------------------
    // Generalised product rule:
    //   d/dx[u·v·w·…] = u'·v·w·… + u·v'·w·… + u·v·w'·… + …
    // -----------------------------------------------------------------------

    case 'mul': {
      const terms = node.children.map((child, i) => {
        const others = node.children.filter((_, j) => j !== i).map(cloneDeep);
        return mul([D(child), ...others]);
      });
      return add(terms);
    }

    // -----------------------------------------------------------------------
    // Power rule — three cases
    // -----------------------------------------------------------------------

    case 'pow': {
      const { base, exp: exponent } = node;

      // Case 1: constant exponent  d/dx[u^c] = c · u^(c-1) · u'
      if (exponent.kind === 'num') {
        const c = exponent.value;
        return mul([num(c), pow(cloneDeep(base), num(c - 1)), D(base)]);
      }

      // Case 2: constant base  d/dx[a^v] = a^v · ln(a) · v'
      if (base.kind === 'num') {
        const a = base.value;
        return mul([cloneDeep(node), num(Math.log(a)), D(exponent)]);
      }

      // Case 3: general u^v  d/dx[u^v] = u^v · (v'·ln(u) + v·u'/u)
      return mul([
        cloneDeep(node),
        add([
          mul([D(exponent), func('ln', [cloneDeep(base)])]),
          mul([cloneDeep(exponent), D(base), pow(cloneDeep(base), num(-1))]),
        ]),
      ]);
    }

    // -----------------------------------------------------------------------
    // Function / chain rule
    // -----------------------------------------------------------------------

    case 'func': {
      return differentiateFunc(node.name, node.args, variable);
    }
  }
}

// ---------------------------------------------------------------------------
// Function derivatives (chain-rule wrappers)
// ---------------------------------------------------------------------------

function differentiateFunc(name: FuncName, args: ExprNode[], variable: string): ExprNode {
  const D = (n: ExprNode): ExprNode => derivative(n, variable);

  // Single-argument functions (the common case)
  const u = args[0]!;
  const up = D(u);

  /** chain(fPrimeOfU, up) = mul([fPrimeOfU, up]) */
  const chain = (fPrimeOfU: ExprNode): ExprNode => mul([fPrimeOfU, up]);

  switch (name) {
    // -----------------------------------------------------------------------
    // Trigonometric
    // -----------------------------------------------------------------------

    case 'sin': {
      // d/dx[sin(u)] = cos(u)·u'
      return chain(func('cos', [cloneDeep(u)]));
    }

    case 'cos': {
      // d/dx[cos(u)] = -sin(u)·u'
      return mul([num(-1), func('sin', [cloneDeep(u)]), up]);
    }

    case 'tan': {
      // d/dx[tan(u)] = cos(u)^(-2)·u'
      return chain(pow(func('cos', [cloneDeep(u)]), num(-2)));
    }

    // -----------------------------------------------------------------------
    // Inverse trigonometric
    // -----------------------------------------------------------------------

    case 'asin': {
      // d/dx[asin(u)] = (1 - u^2)^(-1/2)·u'
      const oneMinusUSq = add([num(1), mul([num(-1), pow(cloneDeep(u), num(2))])]);
      return chain(pow(oneMinusUSq, num(-1 / 2)));
    }

    case 'acos': {
      // d/dx[acos(u)] = -(1 - u^2)^(-1/2)·u'
      const oneMinusUSq = add([num(1), mul([num(-1), pow(cloneDeep(u), num(2))])]);
      return mul([num(-1), pow(oneMinusUSq, num(-1 / 2)), up]);
    }

    case 'atan': {
      // d/dx[atan(u)] = (1 + u^2)^(-1)·u'
      const onePlusUSq = add([num(1), pow(cloneDeep(u), num(2))]);
      return chain(pow(onePlusUSq, num(-1)));
    }

    // -----------------------------------------------------------------------
    // Hyperbolic
    // -----------------------------------------------------------------------

    case 'sinh': {
      // d/dx[sinh(u)] = cosh(u)·u'
      return chain(func('cosh', [cloneDeep(u)]));
    }

    case 'cosh': {
      // d/dx[cosh(u)] = sinh(u)·u'
      return chain(func('sinh', [cloneDeep(u)]));
    }

    case 'tanh': {
      // d/dx[tanh(u)] = (1 - tanh(u)^2)·u'
      const sechSq = add([num(1), mul([num(-1), pow(func('tanh', [cloneDeep(u)]), num(2))])]);
      return chain(sechSq);
    }

    // -----------------------------------------------------------------------
    // Roots
    // -----------------------------------------------------------------------

    case 'sqrt': {
      // d/dx[sqrt(u)] = u' / (2·sqrt(u))  = u' · (2·sqrt(u))^(-1)
      return mul([up, pow(mul([num(2), func('sqrt', [cloneDeep(u)])]), num(-1))]);
    }

    case 'cbrt': {
      // d/dx[cbrt(u)] = u' / (3·cbrt(u)^2)  = u' · (3·cbrt(u)^2)^(-1)
      return mul([up, pow(mul([num(3), pow(func('cbrt', [cloneDeep(u)]), num(2))]), num(-1))]);
    }

    // -----------------------------------------------------------------------
    // Exponential
    // -----------------------------------------------------------------------

    case 'exp': {
      // d/dx[exp(u)] = exp(u)·u'
      return chain(func('exp', [cloneDeep(u)]));
    }

    // -----------------------------------------------------------------------
    // Logarithms
    // -----------------------------------------------------------------------

    case 'log':
    case 'ln': {
      // d/dx[ln(u)] = u'/u  =  u' · u^(-1)
      return mul([up, pow(cloneDeep(u), num(-1))]);
    }

    case 'log2': {
      // d/dx[log2(u)] = u' / (u · ln2)  =  u' · (u · ln2)^(-1)
      return mul([up, pow(mul([cloneDeep(u), num(Math.LN2)]), num(-1))]);
    }

    case 'log10': {
      // d/dx[log10(u)] = u' / (u · ln10)  =  u' · (u · ln10)^(-1)
      return mul([up, pow(mul([cloneDeep(u), num(Math.LN10)]), num(-1))]);
    }

    // -----------------------------------------------------------------------
    // Absolute value / sign
    // -----------------------------------------------------------------------

    case 'abs': {
      // d/dx[|u|] = sign(u)·u'
      return chain(func('sign', [cloneDeep(u)]));
    }

    // -----------------------------------------------------------------------
    // Piecewise-constant: derivative 0 a.e.
    // -----------------------------------------------------------------------

    case 'sign':
    case 'floor':
    case 'ceil':
    case 'round': {
      return num(0);
    }

    // -----------------------------------------------------------------------
    // pow as function form: delegate to the pow-node rule
    // -----------------------------------------------------------------------

    case 'pow': {
      // func('pow', [a, b]) ≡ pow(a, b) — reuse the pow node case
      const a = args[0]!;
      const b = args[1]!;
      return derivative({ kind: 'pow', base: a, exp: b }, variable);
    }

    // -----------------------------------------------------------------------
    // Not symbolically differentiable
    // -----------------------------------------------------------------------

    case 'min':
    case 'max': {
      throw new Error(`derivative: cannot differentiate '${name}'`);
    }
  }
}
